import { ActionKeys } from '~/actions/constants';
import { startCheckout, updateBatchEntriesServiceLevelToken } from '~/actions/orders.actions';
import * as ratesAtCheckoutActions from '~/actions/rates-at-checkout.actions';
import * as shipmentsActions from '~/actions/shipments.actions';
import { AxiosResponse } from 'axios';
import { CheckoutRateAfterPollAttributes } from '~/reducers/orders/orders.reducer';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getOrdersTableData } from '~/selectors/batch-entry.composite.selectors';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import {
  getBatchCurrentStep,
  getSelectedServiceLevelForAllOrders,
} from '~/selectors/orders-batch.selectors';
import { getCheckoutRateAfterPoll } from '~/selectors/orders.selectors';
import { getAllServiceLevelsMapByAccount } from '~/selectors/providers.selectors';
import {
  getIsShippingOptionsRequestComplete,
  getIsShippingOptionsRequestInitiated,
  getShippingOptions,
} from '~/selectors/rates-at-checkout.selectors';
import { getUserTouchedRates } from '~/selectors/rates.selectors';
import { getShipmentsData } from '~/selectors/shipments.selectors';
import { getUserDefaultServiceLevel } from '~/selectors/user.selectors';
import * as ratesAtCheckoutService from '~/services/rates-at-checkout.service';
import * as shipmentsService from '~/services/shipments.service';
import { OrdersRenderData } from '~/types/Order';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchSteps } from '~/types/OrdersBatch';
import { ObjectIdProviderAccountMap } from '~/types/ProviderAccount';
import Rate, {
  attributePopularRateV2,
  getRatesByAttribute,
  getRatesForServiceLevelToken,
  getRatesWithAttributeInDisplayOrder,
} from '~/types/Rate';
import {
  findCheapestMatchedServiceLevelRate,
  getMatchedShippingOption,
  getShipmentDatasetById,
  isIntegratedStore,
  ShippingOptionPayload,
} from '~/types/RatesAtCheckout';
import Shipment, * as shipmentModel from '~/types/Shipment';
import { batchAction } from '~/utils/redux/batch';
import { selectState } from '..';
import {
  RATE_DISPLAYED_EVENT,
  RATES_UPDATED_EVENT,
  TrackingReference,
} from '~/utils/TrackingHelper/types';
import { trackingHelper } from '~/utils/TrackingHelper';
import {
  getRateDisplayedEventData,
  trackRateDisplay,
  trackRateUpdate,
} from '~/utils/TrackingHelper/utils';
import { ShipmentStatus } from '~/types/Shipment';
import CarrierAccount from '~/types/CarrierAccount';
import { getCarrierAccounts } from '~/selectors/carrier-accounts.selectors';

export function* fetchShipment(action: ReturnType<typeof shipmentsActions.fetchShipment>) {
  try {
    const { shipmentId, trackingReference } = action;
    const response: AxiosResponse<shipmentModel.JSONShipment> = yield call(
      shipmentsService.getRates,
      shipmentId,
    );
    const shipment: Shipment = shipmentModel.fromJSON(response.data);

    const payload: shipmentsActions.FetchShipmentPayload = {
      shipment,
    };

    yield call(trackRateUpdate, trackingReference, RATES_UPDATED_EVENT.operations.GENERATED, {
      rateIds: payload.shipment?.rates?.map((rate) => rate.objectId),
      shipmentId: payload.shipment?.objectId,
    });

    yield put(
      batchAction(
        shipmentsActions.fetchShipmentRatesSuccess(shipmentId, payload, trackingReference),
      ),
    );
  } catch (err) {
    yield put(shipmentsActions.fetchShipmentRatesError(action.shipmentId));
  }
}

export function* autoSelectRate(
  action: ReturnType<typeof shipmentsActions.fetchShipmentRatesSuccess>,
) {
  yield autoSelectRateHelper(action.payload.shipment, action.trackingReference);
}

export function* trackRateDisplayed(
  action: ReturnType<typeof shipmentsActions.fetchShipmentRatesSuccess>,
) {
  const {
    payload: { shipment },
    trackingReference,
  } = action;

  const { isBulk, orderQuantity } = trackingReference;

  if (shipment.status !== ShipmentStatus.SUCCESS || isBulk || !orderQuantity) {
    return;
  }

  const { rates } = shipment;

  // update attributes of rates before sorting
  const attributedRates = attributePopularRateV2(rates);

  // get rates in order how they are displayed in UI
  const sortedRates = getRatesWithAttributeInDisplayOrder(attributedRates);
  const carrierAccounts: CarrierAccount[] = yield select(getCarrierAccounts);

  // track each displayed rate
  for (const rate of attributedRates) {
    const eventData = getRateDisplayedEventData(rate, sortedRates, carrierAccounts);
    yield call(
      trackRateDisplay,
      trackingReference,
      RATE_DISPLAYED_EVENT.operations.DISPLAYED,
      eventData,
    );
  }
}

// auto-select rates for all previously-fetched shipments
// currently triggered by receipt of service levels data (which includes data on which service levels
// are "hidden" and should not be selected)
export function* retryAutoSelectRates() {
  const shipmentsData: { [shipmentObjectId: string]: Readonly<Shipment> } =
    yield select(getShipmentsData);
  const trackingReference = trackingHelper.getTrackingReference(
    RATES_UPDATED_EVENT.name,
    'order_table',
    { rateIds: undefined },
  );
  yield all(
    Object.values(shipmentsData).map((shipment) =>
      autoSelectRateHelper(shipment, trackingReference),
    ),
  );
}

export function* autoSelectRateHelper(shipment: Shipment, trackingReference: TrackingReference) {
  const userTouchedRates: Record<string, boolean> =
    yield selectState<Record<string, boolean>>(getUserTouchedRates);
  const checkoutRateAfterPoll: Maybe<CheckoutRateAfterPollAttributes> =
    yield select(getCheckoutRateAfterPoll);
  let autoCheckoutRateFound = false;

  // Check that the shipment hasn't been "touched" by the user
  if (userTouchedRates[shipment.objectId]) return;

  const userDefaultServiceLevel: Maybe<{ provider: string; token: string }> = yield selectState<
    Maybe<{ provider: string; token: string }>
  >(getUserDefaultServiceLevel);

  let rates = shipment.rates;

  const allServiceLevels: ObjectIdProviderAccountMap =
    yield selectState<ObjectIdProviderAccountMap>(getAllServiceLevelsMapByAccount);
  // filter for only active rates (not hidden)
  if (Object.keys(allServiceLevels).length > 0) {
    rates = rates.filter(
      (rate) =>
        !rate?.carrierAccount ||
        !rate?.servicelevel?.token ||
        !allServiceLevels[rate.carrierAccount]?.serviceLevelsByToken[
          rate.servicelevel.extended_token ?? rate.servicelevel.token
        ]?.is_hidden,
    );
  }

  // If there's nothing to autoselect, return
  if (rates.length === 0) return;

  let autoSelectedRate: Rate | undefined;

  // Batch specific behaviour. Find a better way?
  const currentBatchStep: BatchSteps = yield selectState<BatchSteps>(getBatchCurrentStep);
  const serviceLevelForAllOrders: Maybe<string> = yield selectState<Maybe<string>>(
    getSelectedServiceLevelForAllOrders,
  );

  // If checkoutRateAfterPoll is set and the fetched shipment is the one we are looking for then auto-select the desired rate
  if (
    checkoutRateAfterPoll &&
    checkoutRateAfterPoll.currBatchEntry?.shipmentObjectId === shipment.objectId
  ) {
    autoSelectedRate = rates.find(
      (rate: Rate) =>
        rate.servicelevel.token === checkoutRateAfterPoll.serviceLevelToken &&
        checkoutRateAfterPoll.carrierAccount === rate.carrierAccount,
    );

    if (autoSelectedRate) {
      autoCheckoutRateFound = true;
    }
  }

  if (currentBatchStep !== BatchSteps.NONE && serviceLevelForAllOrders && !autoSelectedRate) {
    autoSelectedRate = rates.find(
      (rate: Rate) => rate.servicelevel.token === serviceLevelForAllOrders,
    );
  }

  // Search for a preferred service level on the batch entry corresponding to this shipment
  const batchEntries: HashMap<number, BatchEntryExtended> = yield select(getBatchEntryData);

  const batchEntryWithPreferredServiceLevelToken = Object.values(batchEntries).find(
    (batchEntry: BatchEntryExtended) => {
      return (
        batchEntry.shipmentObjectId === shipment.objectId && batchEntry.preferredServiceLevelToken
      );
    },
  );

  // If any batch entries associated with this shipment have a preferred service level,
  // check if any rates match up with them
  const token = batchEntryWithPreferredServiceLevelToken?.preferredServiceLevelToken;
  if (token) {
    const ratesForServiceLevelToken: Rate[] = getRatesForServiceLevelToken(
      rates,
      token,
      batchEntryWithPreferredServiceLevelToken?.preferredAccountObjectID,
    );
    if (ratesForServiceLevelToken.length > 0) {
      autoSelectedRate = ratesForServiceLevelToken[0];
    }
  }

  const shipmentDatasets: OrdersRenderData[] = yield select(getOrdersTableData);
  const shipmentDataset: Maybe<OrdersRenderData> = getShipmentDatasetById(
    shipment?.objectId,
    shipmentDatasets,
  );

  // check if order is from an integrated store that support Rates at checkout
  const isIntegratedStoreOrder = isIntegratedStore(shipmentDataset);

  // find match for shipping option service level token to returned rates
  if (isIntegratedStoreOrder) {
    let shippingOptions: ShippingOptionPayload[] =
      yield selectState<ShippingOptionPayload[]>(getShippingOptions);
    let isShippingOptionsInitiated: boolean = yield selectState<boolean>(
      getIsShippingOptionsRequestInitiated,
    );
    let isShippingOptionsRequestComplete: boolean = yield selectState<boolean>(
      getIsShippingOptionsRequestComplete,
    );
    if (
      (shippingOptions.length === 0 && !isShippingOptionsInitiated) ||
      !isShippingOptionsRequestComplete
    ) {
      try {
        const response: { data: ShippingOptionPayload[] } = yield call(
          ratesAtCheckoutService.fetchShippingOptions,
        );
        yield put(ratesAtCheckoutActions.fetchShippingOptionsSuccess(response));
        shippingOptions = yield selectState<ShippingOptionPayload[]>(getShippingOptions);
      } catch (err) {
        yield put(ratesAtCheckoutActions.fetchShippingOptionsError(err.message));
      }
    }
    // find order.shippingMethod in ShippingOption.name, return matched option
    const matchedShippingOption = getMatchedShippingOption(shippingOptions, shipmentDataset);

    // find a match for shipping option service level token to rate.
    // if multiple matched, return the cheapest rate.
    const cheapestMatchedRate = findCheapestMatchedServiceLevelRate(
      matchedShippingOption?.service_levels,
      rates,
    );
    if (cheapestMatchedRate && !autoSelectedRate) {
      autoSelectedRate = cheapestMatchedRate;
    }
  }
  // apply automation rules
  const { cheapestRates } = getRatesByAttribute(rates);

  // Search the user default service level
  if (userDefaultServiceLevel && !autoSelectedRate) {
    autoSelectedRate = rates.find(
      (rate: Rate) => rate.servicelevel.token === userDefaultServiceLevel.token,
    );
  }

  // Search cheapest
  if (!autoSelectedRate && cheapestRates.length > 0) {
    autoSelectedRate = cheapestRates[0];
  }

  // There might still be a rate...
  if (!autoSelectedRate) {
    autoSelectedRate = rates[0];
  }

  if (autoSelectedRate) {
    yield put(
      batchAction(
        shipmentsActions.selectRate(
          shipment.objectId,
          autoSelectedRate.objectId,
          false,
          trackingReference,
        ),
      ),
    );
  }

  if (checkoutRateAfterPoll && checkoutRateAfterPoll.currBatchEntry && autoCheckoutRateFound) {
    yield put(
      startCheckout({
        [checkoutRateAfterPoll.currBatchEntry.orderObjectId]: [
          checkoutRateAfterPoll.currBatchEntry.id,
        ],
      }),
    );
  }
}

export function* selectRate(action: ReturnType<typeof shipmentsActions.selectRate>) {
  const {
    orderSelection,
    serviceLevelToken,
    userTriggered,
    accountObjectId,
    trackingReference,
    rateObjectId,
  } = action.payload;
  trackRateUpdate(trackingReference, RATES_UPDATED_EVENT.operations.SELECTED, {
    isAutoSelection: !userTriggered,
    rateId: rateObjectId,
  });
  if (userTriggered && orderSelection && serviceLevelToken) {
    yield put(
      updateBatchEntriesServiceLevelToken(orderSelection, serviceLevelToken, accountObjectId),
    );
  }
}

export function* selectRates(action: ReturnType<typeof shipmentsActions.selectRate>) {
  const { orderSelection, serviceLevelToken, accountObjectId, trackingReference } = action.payload;
  if (orderSelection && serviceLevelToken) {
    trackRateUpdate(trackingReference, RATES_UPDATED_EVENT.operations.SELECTED, {
      isAutoSelection: false, // selecting multiple rates only possible by user trigger
    });
    yield put(
      updateBatchEntriesServiceLevelToken(orderSelection, serviceLevelToken, accountObjectId),
    );
  }
}

export default [
  takeEvery(ActionKeys.SHIPMENT_FETCH, fetchShipment),
  takeEvery(ActionKeys.SHIPMENT_FETCH_SUCCESS, autoSelectRate),
  takeLatest(ActionKeys.SHIPMENT_FETCH_SUCCESS, trackRateDisplayed),
  takeEvery(ActionKeys.RETRY_AUTO_SELECT_RATES, retryAutoSelectRates),
  takeLatest(ActionKeys.RATE_SELECT, selectRate),
  takeLatest(ActionKeys.MULTIPLE_RATE_SELECT, selectRates),
];
